import { Link } from '@tanstack/react-router';
import {
  AppWindow,
  Building2,
  ChevronLeft,
  ChevronRight,
  Film,
  History,
  KeyRound,
  LayoutDashboard,
  MonitorCog,
  ReceiptText,
  Shield,
  UserCog,
  Users,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useUiStore } from '@/store/ui-store';

export type NavKey =
  | 'dashboard'
  | 'accounts'
  | 'transactions'
  | 'apps'
  | 'operators'
  | 'users'
  | 'permissions'
  | 'platforms'
  | 'providers'
  | 'materialsReview'
  | 'materialsDisplay'
  | 'materialProcess'
  | 'auditLogs';

/** 各导航项是否对当前用户可见。缺省的键视为不可见。 */
export type NavPermissions = Partial<Record<NavKey, boolean>>;

interface NavItem {
  key: NavKey;
  label: string;
  to: string;
  icon: ReactNode;
}

const NAV_ITEMS: NavItem[] = [
  { key: 'dashboard', label: '仪表盘', to: '/ads/dashboard', icon: <LayoutDashboard size={18} /> },
  { key: 'accounts', label: '广告账户管理', to: '/ads/accounts', icon: <Users size={18} /> },
  {
    key: 'transactions',
    label: '广告账户流水',
    to: '/ads/ad-account-transactions',
    icon: <ReceiptText size={18} />,
  },
  { key: 'apps', label: 'App 管理', to: '/ads/apps', icon: <AppWindow size={18} /> },
  { key: 'operators', label: '投手管理', to: '/ads/operators', icon: <UserCog size={18} /> },
  { key: 'users', label: '用户管理', to: '/ads/users', icon: <Shield size={18} /> },
  { key: 'permissions', label: '权限管理', to: '/ads/permissions', icon: <KeyRound size={18} /> },
  {
    key: 'platforms',
    label: '广告平台管理',
    to: '/ads/ad-platforms',
    icon: <MonitorCog size={18} />,
  },
  {
    key: 'providers',
    label: '广告户商管理',
    to: '/ads/ad-providers',
    icon: <Building2 size={18} />,
  },
  {
    key: 'materialsReview',
    label: '审核素材',
    to: '/ads/materials/review',
    icon: <Film size={18} />,
  },
  {
    key: 'materialsDisplay',
    label: '投放素材',
    to: '/ads/materials/display',
    icon: <Film size={18} />,
  },
  {
    key: 'materialProcess',
    label: '素材加工',
    to: '/ads/material-process',
    icon: <Film size={18} />,
  },
  { key: 'auditLogs', label: '修改记录', to: '/ads/audit-logs', icon: <History size={18} /> },
];

export function Sidebar({ nav }: { nav: NavPermissions }) {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);

  const items = NAV_ITEMS.filter((item) => nav[item.key]);

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-20 flex h-full flex-col border-r border-border bg-card transition-[width] duration-200',
        collapsed ? 'w-[4.5rem]' : 'w-60',
      )}
    >
      {collapsed ? (
        <div className="flex h-14 items-center justify-center border-b border-border">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-muted-foreground hover:text-foreground"
            aria-label="展开侧栏"
            title="展开侧栏"
            onClick={toggleSidebar}
          >
            <ChevronRight size={18} />
          </Button>
        </div>
      ) : (
        <div className="flex h-14 items-center gap-2.5 border-b border-border px-5">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/15 text-primary">
            <span className="font-mono text-sm font-bold">A</span>
          </div>
          <span className="text-base font-bold tracking-tight text-foreground">AdsAdmin</span>
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto h-8 w-8 text-muted-foreground hover:text-foreground"
            aria-label="收起侧栏"
            title="收起侧栏"
            onClick={toggleSidebar}
          >
            <ChevronLeft size={16} />
          </Button>
        </div>
      )}

      <TooltipProvider delayDuration={0}>
        <nav className={cn('flex-1 space-y-0.5 overflow-y-auto py-3', collapsed ? 'px-2' : 'px-3')}>
          {items.map((item) => {
            const link = (
              <Link
                to={item.to}
                className={cn(
                  'flex items-center rounded-md text-[13px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground',
                  collapsed ? 'mx-auto h-9 w-9 justify-center' : 'gap-2.5 px-3 py-2',
                )}
                activeProps={{
                  className:
                    'bg-primary/10 font-semibold text-primary shadow-[inset_2px_0_0_hsl(var(--primary))]',
                }}
              >
                <span className="shrink-0">{item.icon}</span>
                {!collapsed && item.label}
              </Link>
            );
            return collapsed ? (
              <Tooltip key={item.to}>
                <TooltipTrigger asChild>{link}</TooltipTrigger>
                <TooltipContent side="right">{item.label}</TooltipContent>
              </Tooltip>
            ) : (
              <div key={item.to}>{link}</div>
            );
          })}
        </nav>
      </TooltipProvider>
    </aside>
  );
}
